/**
 * Abre numa aba nova um documento cujo link só se sabe DEPOIS de uma chamada
 * assíncrona (ex.: PDF da fatura relido no gateway).
 *
 * Origem: #783 — 2026-09-29 12:45 +0200 (achado na tela do cliente do CRM, #776).
 *
 * Por quê: o link gravado do PDF do Stripe expira, então o clique pede o link
 * atual à callable `getInvoiceDocumentUrl`. Mas o navegador só deixa abrir aba
 * DURANTE o clique — um `window.open(url)` depois do `await` cai no bloqueador
 * de pop-up. Por isso a aba abre vazia no clique e recebe a URL quando ela
 * chega.
 *
 * Contrato: `resolve()` devolve a URL (ou `null` = sem documento); lança →
 * usa `fallback`. Sem URL nenhuma, a aba vazia é fechada e a função devolve
 * `false` (quem chama avisa). A aba nasce sem `opener` (a página aberta não
 * alcança a nossa). Se o navegador recusar até a aba vazia, abre direto no
 * fim (melhor tentativa).
 *
 * Usado por: CRM `FinanceTab`; admin `InvoiceActions`/`InvoicesPage`; app
 * `InvoicesPage`/`RecentInvoicesSection`.
 *
 * Arquitetura: módulo puro com o `window` injetado — `packages/types` também
 * é compilado nas Functions (sem DOM); o tipo mínimo `TabOpener` evita
 * depender de `lib.dom`. Um lugar só para os três apps (o CRM não depende do
 * `ui-shared`).
 *
 * Issues: #776.
 */
export type TabOpener = {
  open(url: string, target: string, features?: string): OpenedTab | null;
};
export type OpenedTab = {
  opener: unknown;
  location: { href: string };
  close: () => void;
};

export async function openResolvedInNewTab(
  win: TabOpener,
  resolve: () => Promise<string | null>,
  fallback: string | null,
): Promise<boolean> {
  const tab = win.open('', '_blank');
  if (tab) tab.opener = null;
  let url: string | null;
  try {
    url = (await resolve()) ?? fallback;
  } catch {
    url = fallback;
  }
  if (url === null) {
    tab?.close();
    return false;
  }
  if (tab) tab.location.href = url;
  else win.open(url, '_blank', 'noopener,noreferrer');
  return true;
}
